import React from 'react';
import { Link } from 'react-router-dom';
import { Info, WarningCircle } from '@phosphor-icons/react';

import { Icon } from '../../components/Icon';
import { Glyph } from '../../components/shells/glyphs';
import { cn } from '../../lib/cn';
import { useViewport } from '../../hooks/useViewport';

import { Button as DskButton } from '../../components/desktop';
import { Cta } from '../../components/mobile';

import './status.css';

/**
 * The status splash pattern. Five routes share it: payment success, payment
 * failed, registration complete, email verification and the `*` 404.
 *
 * WHAT THIS IS AND IS NOT
 * -----------------------
 * `StatusShell` owns the ANATOMY, and that anatomy is fixed: disc, kicker,
 * headline, one supporting line, an optional body slot, then one primary
 * command and at most one ghost escape hatch. Nothing here changes any of it.
 *
 * What lives here is the four things that drop INTO the body slot (a receipt,
 * a "what happens next" list, a failure reason, a neutral note), the resend
 * form box, and the track-correct action button, because those repeat across
 * the five routes and copying them five times is how the app got here.
 *
 * THE TONE RULE IS NOT RESTATED, IT IS OBEYED
 * -------------------------------------------
 * Outcome colour lives in the disc, which is the shell's. `StatusAction` emits
 * `primary` (ink) or `ghost` and there is deliberately no third variant: no
 * tinted button, no accent fill, no green "View order". The one place colour
 * appears down here is `StatusReason`, which is a bordered block of WORDS and
 * a glyph, so the colour is never carrying the meaning by itself.
 *
 * Everything in this file is presentational. No fetch, no navigate, no timers,
 * no toast.
 */

/* ------------------------------------------------------------------ stack */

/** Wraps two or more detail blocks in the body slot so they get a rhythm. */
export function StatusStack({ className, ...rest }) {
  return <div className={cn('dsa-status__stack', className)} {...rest} />;
}

/* ---------------------------------------------------------------- receipt */

/** The receipt box. Rows only, no heading: the headline above already said it. */
export function StatusReceipt({ className, ...rest }) {
  return <div className={cn('dsa-status__receipt', className)} {...rest} />;
}

/**
 * One receipt row.
 *
 * `total` is the amount row and is the only one that steps up a size. Prices
 * arriving here are already formatted Rands with an `R` prefix, never a dollar
 * sign (README section 9).
 */
export function StatusReceiptRow({ label, value, total = false }) {
  return (
    <div className="dsa-status__receipt-row">
      <span className="dsa-status__receipt-k">{label}</span>
      <span className={cn('dsa-status__receipt-v', total && 'dsa-status__receipt-v--total')}>
        {value}
      </span>
    </div>
  );
}

/* ------------------------------------------------------- what happens next */

/** The "what happens next" list. Three short lines, never process detail. */
export function StatusNext({ label = 'What happens next', children, className, ...rest }) {
  return (
    <div className={cn('dsa-status__next', className)} {...rest}>
      <span className="dsa-status__next-label">{label}</span>
      {children}
    </div>
  );
}

/**
 * One line of it.
 *
 * `icon` is a Phosphor glyph and is drawn on the DESKTOP track only. The mobile
 * `ds-*` track draws its own 24 grid SVGs and has no package, truck or shield
 * among them, so rather than cross the tracks it gets a plain marker. The line
 * of text is the content either way; the glyph was always decorative.
 */
export function StatusNextRow({ icon, children }) {
  const { isDesktop } = useViewport();
  return (
    <span className="dsa-status__next-row">
      {isDesktop && icon ? (
        <Icon as={icon} size="sm" />
      ) : (
        <span className="dsa-status__next-dot" aria-hidden="true" />
      )}
      <span>{children}</span>
    </span>
  );
}

/* ------------------------------------------------------------ reason, note */

/** The leading glyph, per track. Phosphor on desktop, 24 grid SVG on mobile. */
function LeadGlyph({ desktopIcon, mobileGlyph }) {
  const { isDesktop } = useViewport();
  if (isDesktop) return <Icon as={desktopIcon} size="sm" />;
  return (
    <span className="dsa-status__reason-mark" aria-hidden="true">
      <Glyph name={mobileGlyph} />
    </span>
  );
}

/**
 * Why it failed. Border, glyph and words, so colour is never alone.
 *
 * This is not a second disc. It explains, it does not restate the outcome, and
 * it never appears on a splash whose disc is neutral or success.
 */
export function StatusReason({ children, className, ...rest }) {
  if (!children) return null;
  return (
    <div className={cn('dsa-status__reason', className)} role="alert" {...rest}>
      <LeadGlyph desktopIcon={WarningCircle} mobileGlyph="warning" />
      <span>{children}</span>
    </div>
  );
}

/** The same shape on the hairline edge, for a fact that is not a failure. */
export function StatusNote({ children, className, ...rest }) {
  if (!children) return null;
  return (
    <div className={cn('dsa-status__note', className)} role="status" {...rest}>
      <LeadGlyph desktopIcon={Info} mobileGlyph="question" />
      <span>{children}</span>
    </div>
  );
}

/* ------------------------------------------------------------------- form */

/** The box around the one control a splash is allowed: a resend address. */
export function StatusForm({ className, ...rest }) {
  return <div className={cn('dsa-status__form', className)} {...rest} />;
}

/* ---------------------------------------------------------------- actions */

/**
 * One command in the actions slot.
 *
 * Two variants and no more. `primary` is INK, in both themes and on both
 * tracks, whatever the outcome of the page was: the disc already carries the
 * outcome and a button is a command regardless of how the user got here.
 *
 * `to` makes it a real link, so it opens in a new tab and shows its target in
 * the status bar. Without `to` it is a `<button>` driving the callback the
 * container passed down.
 */
export function StatusAction({ to, variant = 'primary', className, children, ...rest }) {
  const { isDesktop } = useViewport();
  const classes = cn('dsa-status__action', className);

  if (isDesktop) {
    return (
      <DskButton
        as={to ? Link : 'button'}
        to={to}
        variant={variant}
        size="lg"
        className={classes}
        {...rest}
      >
        {children}
      </DskButton>
    );
  }

  // The mobile `Cta` is a `<button>` by design, so the link flavour is drawn as
  // an anchor carrying the same `ds-cta` contract. Still one track, still one
  // tree: nothing is rendered twice and nothing is hidden.
  if (to) {
    return (
      <Link
        to={to}
        className={cn(
          'ds-cta',
          variant === 'primary' ? 'ds-cta--solid' : 'ds-cta--ghost',
          'ds-cta--lg',
          classes,
        )}
        {...rest}
      >
        {children}
      </Link>
    );
  }

  return (
    <Cta
      variant={variant === 'primary' ? 'solid' : 'ghost'}
      size="lg"
      className={classes}
      {...rest}
    >
      {children}
    </Cta>
  );
}

/**
 * The auto-redirect readout.
 *
 * A number of seconds, announced politely, and never the only way out: the
 * buttons above it always work. The countdown itself is a timer, so it belongs
 * to the container. Pass `seconds = null` and this renders nothing, which is
 * what a container should do under `prefers-reduced-motion`, because a number
 * that changes every second is motion too.
 */
export function StatusRedirect({ seconds, destination }) {
  if (seconds == null) return null;
  return (
    <p className="dsa-status__redirect" aria-live="polite">
      Taking you to {destination} in {seconds}s
    </p>
  );
}
